"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import { AUTONOMY_PRACTITIONER_NPIS, buildTeReasonContext, generateTeMessage, generateTeReasonText, getMissingInformationSummary, hasAutonomyDiagnosisRules, hasSleepDiagnosis, isOptumServeReferral, requiresHmoReferral } from "@/configuration/groups/autonomy/configuration";
import { AUTONOMY_PRACTITIONER_MAPPINGS } from "@/configuration/groups/autonomy/generated/practitioner-mappings";
import { formatAutonomyRenderingMismatch } from "@/configuration/groups/autonomy/referral-chart-enrichment";
import CoverageCard from "@/src/components/cards/coverage-card/coverage-card";
import PatientCard from "@/src/components/cards/patient-card/patient-card";
import ReferralCard from "@/src/components/cards/referral-card/referral-card";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import { PDFViewer } from "@/src/components/pdf-viewer/pdf-viewer";
import type { AutonomyReferralAssignmentFact, AutonomyReferralChartResult, AutonomyReferralEmailFact, AutonomyReferralEnrichmentFact, AutonomyReferralInstructionsPresentation, AutonomyReferralTelephoneEncounterFact, AutonomyReferralUploadFact } from "@/src/components/server/autonomy-referral-instructions-step-output";
import { Alert, AlertDescription } from "@/src/components/shadcn/ui/alert";
import { Button } from "@/src/components/shadcn/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/src/components/shadcn/ui/dialog";
import { ReferralProviderCards } from "@/src/components/tasks/referral-provider-cards";
import { useProvidersForDiagnostics } from "@/src/lib/data-fetchers/diagnosis-providers-fetchers";
import { fileViewUrl } from "@/src/lib/files/file-view-url";
import { formatDateForFilename } from "@/src/lib/utils/date-and-phone-utils";
import { WebecwTelEncNonCreationReason } from "@/src/lib/workflows/steps/webecw/tel-enc-contract";
import { V2WorkflowType } from "@/src/models/constants/workflow-types";
import { isStatOrUrgentPriority, Referral } from "@/src/models/documents/referral";
import { getPractitionerNameWithCredentials, Practitioner } from "@/src/models/practitioner";
import { Task, TaskSource } from "@/src/models/task";
const AUTONOMY_PRACTITIONER_NPI_BY_EHR_ID: Record<string, string> = Object.fromEntries(Object.entries(AUTONOMY_PRACTITIONER_NPIS).map(([npi, info]) => [info.ehrId, npi]));
interface AutonomyReferralInstructionsProps {
  referral: Referral;
  task: Task;
  faxSenderNumber?: string | null;
  presentation: AutonomyReferralInstructionsPresentation;
}
type AutonomyReferralEhrPresentation = Exclude<AutonomyReferralInstructionsPresentation, {
  mode: V2WorkflowType.REFERRAL_EMAIL_ONLY_WF;
}>;
type AutonomyReferralMessageProps = Omit<AutonomyReferralInstructionsProps, "presentation"> & {
  presentation: AutonomyReferralEhrPresentation;
};
const AUTONOMY_ECW_URL = "https://gacent96bbda5pqn16app.ecwcloud.com/mobiledoc/jsp/webemr/index.jsp";
const AUTONOMY_TE_NON_CREATION_FACTS: Record<WebecwTelEncNonCreationReason, string> = {
  [WebecwTelEncNonCreationReason.NOT_NEEDED]: "Telephone Encounter creation was not needed",
  [WebecwTelEncNonCreationReason.UNKNOWN_CHART_ORIGIN]: "Chart origin could not be confirmed",
  [WebecwTelEncNonCreationReason.EXISTING_TE]: "An existing Telephone Encounter was found",
  [WebecwTelEncNonCreationReason.GUARD_UNAVAILABLE]: "Encounter history could not be confirmed",
  [WebecwTelEncNonCreationReason.UNRESOLVED_ASSIGNEE]: "Staff assignee could not be resolved",
  [WebecwTelEncNonCreationReason.UNRESOLVED_DOCTOR]: "Telephone Encounter doctor could not be resolved",
  [WebecwTelEncNonCreationReason.UNRESOLVED_FACILITY]: "Telephone Encounter facility could not be resolved",
  [WebecwTelEncNonCreationReason.EXPLICIT_CREATE_FAILURE]: "eCW reported that Telephone Encounter creation failed",
  [WebecwTelEncNonCreationReason.UNCONFIRMED_CREATE_RESULT]: "Telephone Encounter creation result could not be confirmed"
};
function findOutboundFaxFile(task: Task) {
  return task.files.find(file => file.source === TaskSource.FAX_OUTBOUND);
}
function getProviderName(provider: Practitioner | null, fallback: string): string {
  if (provider) return getPractitionerNameWithCredentials(provider) || fallback;else return fallback;
}
function AssignmentNote() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="text-xs text-muted-foreground italic">Note: Verify assignment based on patient's last encounter provider</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function getChartResultText(chart: AutonomyReferralChartResult): string {
  return chart.created ? "Patient chart created in eCW" : "Existing patient chart updated in eCW";
}
function AutonomyReferralProviderReview(t0) {
  const $ = _c(7);
  const {
    enrichment
  } = t0;
  if (!enrichment.available) {
    return null;
  }
  let t1;
  if ($[0] !== enrichment.providerVerificationRequired) {
    t1 = enrichment.providerVerificationRequired && <Alert className="mt-2"><AlertDescription className="text-sm">Provider enrichment requires review; verify the referring and rendering provider fields in eCW.</AlertDescription></Alert>;
    $[0] = enrichment.providerVerificationRequired;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== enrichment.renderingMismatch) {
    t2 = enrichment.renderingMismatch && <Alert className="mt-2"><AlertDescription className="text-sm">⚠️ {formatAutonomyRenderingMismatch(enrichment.renderingMismatch)}</AlertDescription></Alert>;
    $[2] = enrichment.renderingMismatch;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  return t3;
}
function AutonomyReferralUploadResult(t0) {
  const $ = _c(5);
  const {
    assignment,
    upload
  } = t0;
  if (!upload.available) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <>Document upload result unavailable</>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (!assignment.available) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <>Document uploaded; assignment result unavailable</>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  if (!assignment.resolved) {
    let t1;
    if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <>The document was uploaded without the intended Staff assignment; assign it to the correct reviewer in eCW.</>;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] !== assignment.name) {
    t1 = <>Uploaded and assigned to <CopyableContent text={assignment.name} /></>;
    $[3] = assignment.name;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function AutonomyReferralEmailMessage(t0) {
  const $ = _c(3);
  const {
    email
  } = t0;
  const result = !email.available ? "Email forwarding result unavailable" : email.sent ? "Referral forwarded by email" : "Referral email was not sent \u2014 route the referral manually";
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <p className="font-medium text-center mb-4">Autonomy Health Referral Instructions:</p>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== result) {
    t2 = <div className="flex flex-col gap-1">{t1}<div className="text-muted-foreground">{result}</div></div>;
    $[1] = result;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
function AutonomyReferralTelephoneEncounterResult(t0) {
  const $ = _c(12);
  const {
    telephoneEncounter
  } = t0;
  if (!telephoneEncounter.available) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <>Telephone Encounter automation result is unavailable</>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (telephoneEncounter.outcome.created) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <>eCW confirmed the Telephone Encounter was created</>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  const reason = telephoneEncounter.outcome.reason;
  const t1 = AUTONOMY_TE_NON_CREATION_FACTS[reason];
  let t2;
  if ($[2] !== t1) {
    t2 = <div>{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== telephoneEncounter.reviewAction) {
    t3 = telephoneEncounter.reviewAction === WebecwTelEncNonCreationReason.EXISTING_TE && <div><span className="font-medium">Action:</span> Append referral-authorization information to the existing Telephone Encounter and preserve its existing owner.</div>;
    $[4] = telephoneEncounter.reviewAction;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== telephoneEncounter.reviewAction) {
    t4 = telephoneEncounter.reviewAction === WebecwTelEncNonCreationReason.UNCONFIRMED_CREATE_RESULT && <div><span className="font-medium">Action:</span> Verify in eCW whether the Telephone Encounter already exists before creating one.</div>;
    $[6] = telephoneEncounter.reviewAction;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t2 || $[9] !== t3 || $[10] !== t4) {
    t5 = <div className="space-y-2">{t2}{t3}{t4}</div>;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}
function AutonomyReferralMessage(t0) {
  const $ = _c(98);
  const {
    referral,
    task,
    faxSenderNumber: t1,
    presentation
  } = t0;
  const faxSenderNumber = t1 === undefined ? null : t1;
  let t2;
  if ($[0] !== referral || $[1] !== task) {
    const teContext = buildTeReasonContext(referral, task, null);
    t2 = generateTeReasonText(teContext);
    $[0] = referral;
    $[1] = task;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const teReason = t2;
  let t3;
  if ($[3] !== faxSenderNumber || $[4] !== referral || $[5] !== task) {
    t3 = generateTeMessage(referral, task, faxSenderNumber);
    $[3] = faxSenderNumber;
    $[4] = referral;
    $[5] = task;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const teMessage = t3;
  const missingInfo = getMissingInformationSummary(referral, task);
  let t4;
  if ($[7] !== referral) {
    t4 = requiresHmoReferral(referral);
    $[7] = referral;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const needsHmoReferral = t4;
  let t5;
  if ($[9] !== referral.schedulingPriority) {
    t5 = isStatOrUrgentPriority(referral.schedulingPriority);
    $[9] = referral.schedulingPriority;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const isUrgent = t5;
  let t6;
  if ($[11] !== referral) {
    t6 = hasSleepDiagnosis(referral);
    $[11] = referral;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const isSleepDiagnosis = t6;
  let t7;
  if ($[13] !== faxSenderNumber || $[14] !== referral) {
    t7 = isOptumServeReferral(faxSenderNumber, referral);
    $[13] = faxSenderNumber;
    $[14] = referral;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const isOptumServe = t7;
  const {
    data: matchedProviders
  } = useProvidersForDiagnostics(referral.diagnosisList ?? null);
  const outboundFaxFile = findOutboundFaxFile(task);
  const [dialogOpen, setDialogOpen] = useState(false);
  let t8;
  if ($[16] !== referral.dateReceived) {
    t8 = formatDateForFilename(referral.dateReceived, "");
    $[16] = referral.dateReceived;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== referral.referringProvider) {
    t9 = getProviderName(referral.referringProvider, "Unknown Provider");
    $[18] = referral.referringProvider;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const fileName = `${t8} Referral from ${t9}`;
  let t10;
  if ($[20] !== referral.receivingProvider) {
    t10 = getProviderName(referral.receivingProvider, "Rendering Provider");
    $[20] = referral.receivingProvider;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const renderingProviderName = t10;
  const npi = referral.receivingProvider?.npiNumber ?? null;
  const supervisorLeadMa = (npi ? AUTONOMY_PRACTITIONER_MAPPINGS[npi] : null)?.supervisorLeadMa || null;
  const staffName = isSleepDiagnosis ? "Nicole Dunlap" : supervisorLeadMa;
  let t11;
  if ($[22] !== matchedProviders) {
    t11 = new Set((matchedProviders ?? []).map(_temp).filter(_temp2));
    $[22] = matchedProviders;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  const matchedNpis = t11;
  let t12;
  if ($[24] !== isSleepDiagnosis || $[25] !== matchedNpis || $[26] !== matchedProviders?.length || $[27] !== npi) {
    t12 = !isSleepDiagnosis && hasAutonomyDiagnosisRules(npi) && !!matchedProviders?.length && !matchedNpis.has(npi);
    $[24] = isSleepDiagnosis;
    $[25] = matchedNpis;
    $[26] = matchedProviders?.length;
    $[27] = npi;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  const hasProviderConflict = t12;
  let t13;
  let t14;
  let t15;
  let t16;
  let t17;
  let t18;
  let t19;
  let t20;
  let t21;
  if ($[29] !== fileName || $[30] !== hasProviderConflict || $[31] !== isSleepDiagnosis || $[32] !== isUrgent || $[33] !== matchedProviders || $[34] !== presentation.assignment || $[35] !== presentation.chart || $[36] !== presentation.enrichment || $[37] !== presentation.mode || $[38] !== presentation.telephoneEncounter || $[39] !== presentation.upload || $[40] !== referral.schedulingPriority || $[41] !== renderingProviderName || $[42] !== staffName || $[43] !== teMessage || $[44] !== teReason) {
    const alternativeProviderNames = (matchedProviders ?? []).map(_temp3);
    t20 = "flex flex-col gap-1";
    if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
      t21 = <p className="font-medium text-center mb-4">Autonomy Health Referral Instructions:</p>;
      $[54] = t21;
    } else {
      t21 = $[54];
    }
    t13 = "text-muted-foreground flex flex-col gap-2";
    if ($[55] !== fileName || $[56] !== presentation.assignment || $[57] !== presentation.chart || $[58] !== presentation.enrichment || $[59] !== presentation.mode || $[60] !== presentation.upload || $[61] !== renderingProviderName || $[62] !== staffName) {
      t14 = presentation.mode === V2WorkflowType.INBOUND_REFERRAL_WF ? <><div className="flex flex-col gap-2"><span className="font-semibold">1. {getChartResultText(presentation.chart)}:{" "}<a href={AUTONOMY_ECW_URL} target="_blank" rel="noopener noreferrer" className="hover:bg-accent rounded p-1">Link</a></span><div className="ml-4 space-y-1 text-xs"><div>Complete: Pharmacy, Additional Information, Responsible Party sections</div><div>Run insurance eligibility if plan type is unclear</div></div></div><AutonomyReferralProviderReview enrichment={presentation.enrichment} /><div className="flex flex-col gap-2"><span className="font-semibold">2. Document upload</span><div className="ml-4"><AutonomyReferralUploadResult assignment={presentation.assignment} upload={presentation.upload} /></div></div></> : <><div className="flex flex-col gap-2"><span className="font-semibold">1. Create or Update the patient in eCW:{" "}<a href={AUTONOMY_ECW_URL} target="_blank" rel="noopener noreferrer" className="hover:bg-accent rounded p-1">Link</a></span><div className="ml-4 space-y-1 text-xs"><div>Complete: Pharmacy, Additional Information, Responsible Party sections</div><div>Run insurance eligibility if plan type is unclear</div></div></div><div className="flex flex-col gap-2"><span className="font-semibold">2. Upload the file to the proper folder</span><div className="ml-4 space-y-2"><div><span className="font-medium">Document Type:</span> <CopyableContent text="Patient Referral" /></div><div><span className="font-medium">ECW Folder:</span> <CopyableContent text="Outside Records->Referrals" /></div><div><span className="font-medium">File name:</span> <CopyableContent text={fileName} /></div><div className="space-y-1"><div><span className="font-medium">Assign to (receiving provider):</span>{" "}<CopyableContent text={staffName ?? `${renderingProviderName} - Staff`} /></div><AssignmentNote /></div><div className="text-xs text-muted-foreground italic">BCBS referrals: leave document open. All other insurance: mark as reviewed.</div></div></div></>;
      $[55] = fileName;
      $[56] = presentation.assignment;
      $[57] = presentation.chart;
      $[58] = presentation.enrichment;
      $[59] = presentation.mode;
      $[60] = presentation.upload;
      $[61] = renderingProviderName;
      $[62] = staffName;
      $[63] = t14;
    } else {
      t14 = $[63];
    }
    let t22;
    if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <span className="font-semibold">3. Telephone Encounter</span>;
      $[64] = t22;
    } else {
      t22 = $[64];
    }
    if ($[65] !== presentation.mode || $[66] !== presentation.telephoneEncounter || $[67] !== renderingProviderName || $[68] !== staffName || $[69] !== teMessage || $[70] !== teReason) {
      t15 = <div className="flex flex-col gap-2">{t22}{presentation.mode === V2WorkflowType.INBOUND_REFERRAL_WF ? <div className="ml-4"><AutonomyReferralTelephoneEncounterResult telephoneEncounter={presentation.telephoneEncounter} /></div> : <div className="ml-4 space-y-3"><div><div className="font-medium">Assignment (receiving provider)</div><div className="ml-2 text-sm space-y-1"><div><CopyableContent text={staffName ?? `${renderingProviderName} - Staff`} /></div><AssignmentNote /></div></div><div><div className="font-medium">Reason</div><div className="ml-2 bg-muted/50 p-2 rounded"><CopyableContent text={teReason} /></div><div className="ml-2 mt-1 text-xs text-muted-foreground italic">Add NP (New Patient) or EP (Established Patient) prefix based on encounter history</div></div><div><div className="font-medium">Message</div><div className="ml-2 bg-muted/50 p-3 rounded"><div className="text-sm whitespace-pre-wrap font-mono">{teMessage}</div></div></div></div>}</div>;
      $[65] = presentation.mode;
      $[66] = presentation.telephoneEncounter;
      $[67] = renderingProviderName;
      $[68] = staffName;
      $[69] = teMessage;
      $[70] = teReason;
      $[71] = t15;
    } else {
      t15 = $[71];
    }
    if ($[72] !== isUrgent || $[73] !== referral.schedulingPriority) {
      t16 = isUrgent && <Alert className="mt-2"><AlertDescription className="text-sm">⚠️ Scheduling priority is {referral.schedulingPriority}. Mark the Telephone Encounter as High Priority in eCW.</AlertDescription></Alert>;
      $[72] = isUrgent;
      $[73] = referral.schedulingPriority;
      $[74] = t16;
    } else {
      t16 = $[74];
    }
    if ($[75] !== isSleepDiagnosis) {
      t17 = isSleepDiagnosis && <Alert className="mt-2"><AlertDescription className="text-sm">Sleep-related diagnosis — Use Eslin Luka as rendering provider.</AlertDescription></Alert>;
      $[75] = isSleepDiagnosis;
      $[76] = t17;
    } else {
      t17 = $[76];
    }
    if ($[77] !== isSleepDiagnosis) {
      t18 = isSleepDiagnosis && <Alert className="mt-2"><AlertDescription className="text-sm">Sleep-related diagnosis — Assign referral document and Telephone Encounter to Nicole Dunlap.</AlertDescription></Alert>;
      $[77] = isSleepDiagnosis;
      $[78] = t18;
    } else {
      t18 = $[78];
    }
    t19 = hasProviderConflict && <Alert className="mt-2"><AlertDescription className="text-sm">⚠️ {renderingProviderName} doesn't typically accept this diagnosis per the scheduling guide. Consider: {alternativeProviderNames.join(", ")}.</AlertDescription></Alert>;
    $[29] = fileName;
    $[30] = hasProviderConflict;
    $[31] = isSleepDiagnosis;
    $[32] = isUrgent;
    $[33] = matchedProviders;
    $[34] = presentation.assignment;
    $[35] = presentation.chart;
    $[36] = presentation.enrichment;
    $[37] = presentation.mode;
    $[38] = presentation.telephoneEncounter;
    $[39] = presentation.upload;
    $[40] = referral.schedulingPriority;
    $[41] = renderingProviderName;
    $[42] = staffName;
    $[43] = teMessage;
    $[44] = teReason;
    $[45] = t13;
    $[46] = t14;
    $[47] = t15;
    $[48] = t16;
    $[49] = t17;
    $[50] = t18;
    $[51] = t19;
    $[52] = t20;
    $[53] = t21;
  } else {
    t13 = $[45];
    t14 = $[46];
    t15 = $[47];
    t16 = $[48];
    t17 = $[49];
    t18 = $[50];
    t19 = $[51];
    t20 = $[52];
    t21 = $[53];
  }
  let t22;
  if ($[79] !== needsHmoReferral) {
    t22 = needsHmoReferral && <Alert className="mt-2"><AlertDescription className="text-sm">⚠️ Patient Needs PCP/HMO Referral. Please contact referring provider.</AlertDescription></Alert>;
    $[79] = needsHmoReferral;
    $[80] = t22;
  } else {
    t22 = $[80];
  }
  let t23;
  if ($[81] !== isOptumServe) {
    t23 = isOptumServe && <Alert className="mt-2"><AlertDescription className="text-sm">Optum Serve referral; only Gincy Samuel, MD accepts these referrals</AlertDescription></Alert>;
    $[81] = isOptumServe;
    $[82] = t23;
  } else {
    t23 = $[82];
  }
  const t24 = missingInfo.length > 0 && <Alert className="mt-2"><AlertDescription className="text-sm"><div>ℹ️ Requested the following - please check if they've arrived before reaching out to the provider:</div><ul className="list-disc list-inside space-y-1 mt-2">{missingInfo.map(_temp4)}</ul>{outboundFaxFile && <Dialog open={dialogOpen} onOpenChange={setDialogOpen}><DialogTrigger asChild={true}><Button variant="outline" size="sm" className="mt-3">View Outbound Fax</Button></DialogTrigger><DialogContent className="max-w-4xl h-[80vh]"><DialogHeader><DialogTitle>Outbound Fax - Missing Information Request</DialogTitle></DialogHeader><div className="flex-1 overflow-hidden"><PDFViewer url={fileViewUrl(outboundFaxFile.pathToFile)} /></div></DialogContent></Dialog>}</AlertDescription></Alert>;
  let t25;
  if ($[83] !== t13 || $[84] !== t14 || $[85] !== t15 || $[86] !== t16 || $[87] !== t17 || $[88] !== t18 || $[89] !== t19 || $[90] !== t22 || $[91] !== t23 || $[92] !== t24) {
    t25 = <div className={t13}>{t14}{t15}{t16}{t17}{t18}{t19}{t22}{t23}{t24}</div>;
    $[83] = t13;
    $[84] = t14;
    $[85] = t15;
    $[86] = t16;
    $[87] = t17;
    $[88] = t18;
    $[89] = t19;
    $[90] = t22;
    $[91] = t23;
    $[92] = t24;
    $[93] = t25;
  } else {
    t25 = $[93];
  }
  let t26;
  if ($[94] !== t20 || $[95] !== t21 || $[96] !== t25) {
    t26 = <div className={t20}>{t21}{t25}</div>;
    $[94] = t20;
    $[95] = t21;
    $[96] = t25;
    $[97] = t26;
  } else {
    t26 = $[97];
  }
  return t26;
}
function _temp4(item) {
  return <li key={item}>{item}</li>;
}
function _temp3(p_0) {
  return p_0.name;
}
function _temp2(matched) {
  return !!matched;
}
function _temp(p) {
  return AUTONOMY_PRACTITIONER_NPI_BY_EHR_ID[p.ehrId];
}
function AutonomyReferralCards(t0) {
  const $ = _c(15);
  const {
    referral
  } = t0;
  let t1;
  if ($[0] !== referral.patient) {
    t1 = <PatientCard patient={referral.patient} />;
    $[0] = referral.patient;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  let t3;
  if ($[2] !== referral) {
    t2 = <ReferralProviderCards referral={referral} />;
    t3 = <ReferralCard referral={referral} isEditable={true} />;
    $[2] = referral;
    $[3] = t2;
    $[4] = t3;
  } else {
    t2 = $[3];
    t3 = $[4];
  }
  let t4;
  if ($[5] !== referral.primaryCoverage) {
    t4 = referral.primaryCoverage && <CoverageCard coverage={referral.primaryCoverage} type="Primary" isEditable={true} />;
    $[5] = referral.primaryCoverage;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== referral.secondaryCoverage) {
    t5 = referral.secondaryCoverage && <CoverageCard coverage={referral.secondaryCoverage} type="Secondary" isEditable={true} />;
    $[7] = referral.secondaryCoverage;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t1 || $[10] !== t2 || $[11] !== t3 || $[12] !== t4 || $[13] !== t5) {
    t6 = <>{t1}{t2}{t3}{t4}{t5}</>;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  return t6;
}
export function AutonomyReferralInstructions(t0) {
  const $ = _c(12);
  const {
    referral,
    task,
    faxSenderNumber: t1,
    presentation
  } = t0;
  const faxSenderNumber = t1 === undefined ? null : t1;
  let t2;
  if ($[0] !== faxSenderNumber || $[1] !== presentation || $[2] !== referral || $[3] !== task) {
    t2 = presentation.mode === V2WorkflowType.REFERRAL_EMAIL_ONLY_WF ? <AutonomyReferralEmailMessage email={presentation.email} /> : <AutonomyReferralMessage referral={referral} task={task} faxSenderNumber={faxSenderNumber} presentation={presentation} />;
    $[0] = faxSenderNumber;
    $[1] = presentation;
    $[2] = referral;
    $[3] = task;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const instructions = t2;
  let t3;
  if ($[5] !== instructions) {
    t3 = <EhrInstructionsCard><div className="text-sm text-muted-foreground">{instructions}</div></EhrInstructionsCard>;
    $[5] = instructions;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== referral) {
    t4 = <AutonomyReferralCards referral={referral} />;
    $[7] = referral;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <div className="flex flex-col gap-8 sm:min-w-[800px] w-full max-w-md mx-auto">{t3}{t4}</div>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  return t5;
}